import { Outlet } from "react-router-dom";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useState, useEffect } from "react";
import { useDarkMode } from "../../contexts/ThemeContext";
import { useRole } from "../../contexts/RoleContext";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faHandHoldingHeart,
  faTruckFast,
} from "@fortawesome/free-solid-svg-icons";
import RoleSwitcher from "./RoleSwitcher";
import SharedNav from "./SharedNav";
import DonorNav from "./DonorNav";
import ReceiverNav from "./ReceiverNav";
import Header from "../Header/Header";
import { ROLE_COLORS } from "../../constants/roles";
import routing from "../../routing";
import PunarLogoLight from "../../assets/logos/Punar_dark_mode.png";
import "../../style/SideBar.css";

// the final sidebar comprised of the Donor/Receiver Nav + Shared Nav + RoleSwitcher toggle
function DynamicSideBar() {
  const { currentRole, switchRole, refreshRole } = useRole();
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // check user permissions
  const canDonate = user?.can_donate === 1 || user?.can_donate === true;
  const canReceive = user?.can_receive === 1 || user?.can_receive === true;
  const hasBothRoles = canDonate && canReceive;

  // refresh role when user changes (e.g., after login)
  useEffect(() => {
    if (user) {
      refreshRole();
    }
  }, [user, refreshRole]);

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const toggleRole = () => {
    const newRole = currentRole === "donor" ? "receiver" : "donor";
    switchRole(newRole);

    // check if currently on a role-specific page
    const isOnDonorPage = location.pathname.startsWith(routing.donor.root);
    const isOnReceiverPage = location.pathname.startsWith("/receiver");

    // if switching from donor to receiver and on a donor page, go home
    if (newRole === "receiver" && isOnDonorPage) {
      navigate(routing.home.path);
    }

    // if switching from receiver to donor and on a receiver page, go home
    if (newRole === "donor" && isOnReceiverPage) {
      navigate(routing.home.path);
    }
  };

  const currentColors = ROLE_COLORS[currentRole];

  return (
    <Box
      sx={{
        display: "flex",
        flexGrow: 1,
        minHeight: 0,
        overflow: "hidden",
        height: "100%",
      }}
      className={`${darkMode ? "dark-mode" : "light-mode"} ${currentRole}-role`}
    >
      {/* sidebar */}
      <Box
        sx={{
          width: collapsed ? 60 : 240,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
          transition: "width 0.3s ease",
        }}
        className="sidebar"
      >
        {/* logo and collapse button — always dark */}
        <Box
          sx={{
            height: 72,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            padding: collapsed ? 0 : "0 16px 0 16px",
            paddingRight: collapsed ? 0 : "20px",
            borderBottom: "1px solid #333",
            backgroundColor: "#2a2a2a",
            flexShrink: 0,
            transition: "all 0.3s ease",
          }}
        >
          {/* always use light logo (white text) since sidebar is always dark */}
          {!collapsed && (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <img
                src={PunarLogoLight}
                alt="Punar Logo"
                style={{
                  maxHeight: "48px",
                  width: "auto",
                  objectFit: "contain",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              />
            </Box>
          )}

          {/* Collapse button */}
          <IconButton
            onClick={toggleSidebar}
            size="small"
            sx={{
              color: "#e0e0e0",
              width: "32px",
              height: "32px",
              "&:hover": {
                backgroundColor: "#3a3a3a",
              },
            }}
          >
            <FontAwesomeIcon
              icon={collapsed ? faChevronRight : faChevronLeft}
              style={{ fontSize: "16px" }}
            />
          </IconButton>
        </Box>

        {/* navigation area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* role toggle/switcher - only show if user has both roles */}
          {hasBothRoles && (
            collapsed ? (
              // when collapsed, show compact role toggle button
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "72px",
                  borderBottom: "1px solid #333",
                  backgroundColor: "#2a2a2a",
                }}
              >
                <Tooltip
                  title={
                    currentRole === "donor"
                      ? t("Switch to Receiver")
                      : t("Switch to Donor")
                  }
                  placement="right"
                >
                  <IconButton
                    onClick={toggleRole}
                    size="small"
                    sx={{
                      color: "#e0e0e0",
                      backgroundColor: currentColors.primary,
                      width: "36px",
                      height: "36px",
                      "&:hover": {
                        backgroundColor: currentColors.primary,
                        opacity: 0.9,
                      },
                    }}
                  >
                    <FontAwesomeIcon
                      icon={
                        currentRole === "donor" ? faHandHoldingHeart : faTruckFast
                      }
                      style={{ fontSize: "16px", color: "white" }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              // when expanded, show full roleswitcher
              <RoleSwitcher />
            )
          )}

          {/* shared navigation */}
          <SharedNav collapsed={collapsed} />

          {/* role-specific navigation */}
          {currentRole === "donor" ? (
            <DonorNav collapsed={collapsed} />
          ) : (
            <ReceiverNav collapsed={collapsed} />
          )}
        </Box>
      </Box>

      {/* main content with header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          minHeight: 0,
          overflow: "hidden",
          height: "100%",
        }}
      >
        {/* header at the top */}
        <Header />

        {/* page content below header */}
        <Box className={`main-content ${currentRole}-role`}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

export default DynamicSideBar;
